import React, { createContext, useCallback, useContext, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { FEATURE_FLAGS, useFeatureFlag } from '../../hooks/useFeatureFlag';

// Permission that grants the new (v2) tabbed company profile. Users with it can
// opt in to v2 via the banner toggle; everyone else only ever sees v1.
export const CAN_VIEW_V2_PERMISSION = FEATURE_FLAGS.V2_COMPANY_PROFILE;
const OVERRIDE_KEY = 'tpn:showV2Profile';

interface ProfileVersionState {
  // Whether the viewer is allowed to use (and toggle) the v2 profile.
  canToggle: boolean;
  showV2: boolean;
  setShowV2: (value: boolean) => void;
}

const ProfileVersionContext = createContext<ProfileVersionState>({
  canToggle: false,
  showV2: false,
  setShowV2: () => undefined,
});

export const ProfileVersionProvider = ProfileVersionContext.Provider;

/**
 * Resolves which profile version to show. Users with the v2 permission default
 * to v2 and may revert to v1 (persisted in localStorage) via the banner; users
 * without the permission always get v1. Called by the gate; shared with the
 * banner via context.
 */
export function useProfileVersionValue(): ProfileVersionState {
  const permittedV2 = useFeatureFlag(CAN_VIEW_V2_PERMISSION);

  const [override, setOverride] = useState<boolean | null>(() => {
    const stored = localStorage.getItem(OVERRIDE_KEY);
    return stored === null ? null : stored === 'true';
  });

  const setShowV2 = useCallback((value: boolean) => {
    localStorage.setItem(OVERRIDE_KEY, String(value));
    setOverride(value);
  }, []);

  // Everyone defaults to the original (v1) profile. Permitted users may opt in
  // to v2 via the banner (persisted in localStorage); others are pinned to v1.
  const showV2 = permittedV2 ? override ?? false : false;
  return { canToggle: permittedV2, showV2, setShowV2 };
}

/**
 * Banner rendered at the top of the company profile for users permitted to use
 * v2, letting them revert to the original profile (or switch back). Self-hides
 * for everyone else, so it can be dropped into both profile versions unconditionally.
 */
export function ProfileVersionBanner() {
  const { canToggle, showV2, setShowV2 } = useContext(ProfileVersionContext);
  if (!canToggle) return null;
  return (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Button color="inherit" size="small" onClick={() => setShowV2(!showV2)}>
          {showV2 ? 'Revert to original' : 'Use new profile'}
        </Button>
      }
    >
      You&apos;re viewing the {showV2 ? 'new' : 'original'} company profile.
    </Alert>
  );
}
